import { useState, type ReactNode } from "react";

export interface KanbanColumn<K extends string> {
  key: K;
  label: string;
  /** Header dot color. */
  color: string;
}

/**
 * Generic drag-and-drop kanban, extracted from the dev board so every board
 * in the portal (tickets, deals, …) shares one look and behavior. The card
 * chrome (border, hover, drag ghosting) lives here; `renderCard` supplies
 * only the inner content.
 */
export function Kanban<T, K extends string>({
  columns,
  items,
  idOf,
  columnOf,
  onMove,
  onCardClick,
  renderCard,
  sortItems,
  columnFooter,
}: {
  columns: KanbanColumn<K>[];
  items: T[];
  idOf: (item: T) => string;
  columnOf: (item: T) => K;
  onMove: (id: string, column: K) => void;
  onCardClick?: (item: T) => void;
  renderCard: (item: T) => ReactNode;
  /** Order within a column (defaults to input order). */
  sortItems?: (a: T, b: T) => number;
  /** Optional line under a column header, e.g. a value rollup. */
  columnFooter?: (columnItems: T[]) => ReactNode;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<K | null>(null);

  const dragItem = dragId ? items.find((x) => idOf(x) === dragId) : null;

  function onDrop(col: K) {
    if (dragId) {
      const item = items.find((x) => idOf(x) === dragId);
      if (item && columnOf(item) !== col) onMove(dragId, col);
    }
    setDragId(null);
    setOverCol(null);
  }

  return (
    <div className="flex items-start gap-3 overflow-x-auto pb-4">
      {columns.map((col) => {
        const colItems = items.filter((t) => columnOf(t) === col.key);
        if (sortItems) colItems.sort(sortItems);
        // Only light up columns the dragged card could actually move to.
        const isOver =
          overCol === col.key && !!dragItem && columnOf(dragItem) !== col.key;
        return (
          <div
            key={col.key}
            onDragOver={(e) => {
              e.preventDefault();
              setOverCol(col.key);
            }}
            onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
            onDrop={() => onDrop(col.key)}
            className="flex min-h-[16rem] w-[17rem] shrink-0 flex-col rounded-token-lg bg-bg-secondary/70 p-2 transition-all"
            style={{
              boxShadow: isOver ? `inset 0 0 0 2px ${col.color}` : undefined,
              background: isOver ? `${col.color}0d` : undefined,
            }}
          >
            {/* header */}
            <div
              className="mb-2 flex items-center gap-2 rounded-token-md px-2 py-1.5"
              style={{ borderTop: `2px solid ${col.color}` }}
            >
              <span className="h-2 w-2 rounded-full" style={{ background: col.color }} />
              <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                {col.label}
              </span>
              <span
                className="ml-auto rounded-full px-1.5 py-px text-[11px] font-semibold"
                style={{ background: `${col.color}1a`, color: col.color }}
              >
                {colItems.length}
              </span>
            </div>
            {columnFooter && colItems.length > 0 && (
              <div className="mb-2 px-2 text-[11px] font-medium text-text-muted">
                {columnFooter(colItems)}
              </div>
            )}

            {/* cards */}
            <div className="flex flex-1 flex-col gap-2">
              {colItems.map((item) => {
                const id = idOf(item);
                const isDragging = dragId === id;
                return (
                  <button
                    key={id}
                    draggable
                    onDragStart={() => setDragId(id)}
                    onDragEnd={() => {
                      setDragId(null);
                      setOverCol(null);
                    }}
                    onClick={() => onCardClick?.(item)}
                    className={`group cursor-grab rounded-token-md border bg-bg-card p-3 text-left shadow-token-sm transition-all active:cursor-grabbing ${
                      isDragging
                        ? "border-dashed border-accent opacity-40"
                        : "border-border hover:-translate-y-px hover:border-accent/60 hover:shadow-token-md"
                    }`}
                  >
                    {renderCard(item)}
                  </button>
                );
              })}

              {colItems.length === 0 && (
                <div
                  className={`grid flex-1 place-items-center rounded-token-md border border-dashed text-xs transition-colors ${
                    isOver ? "border-transparent" : "border-border-light"
                  }`}
                  style={{ color: isOver ? col.color : "var(--color-text-muted)", minHeight: 72 }}
                >
                  {dragItem ? "Drop here" : "Empty"}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
